import { z } from "zod";
import { connectorProviderSchema } from "./connectors.js";
/**
 * Twin entities: the things one organisation's business is about (an artist, a promoter company, an
 * engagement, a counterparty), derived from connector data through identity markers on projection
 * contracts and linked within and across systems. The entity layer owns identity: what exists, its
 * names and spellings, which rows are the same thing and how things link. Governed products keep
 * owning numbers. See docs/specs/twin-build-ontology.md.
 */
/**
 * Said wherever Twin entities enter an agent's context: their ids and system record ids let tools and
 * governed filters find them exactly, but people only ever see names.
 */
export const TWIN_ENTITY_HANDLE_GUIDANCE = "Ids (ent_…) and system record ids are handles for tool calls and query " +
    "filters, never for people: name each entity by its display name and leave ids out of answers.";
const slugSchema = z.string().regex(/^[a-z][a-z0-9_]{0,62}$/u);
const isoDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/u);
const columnSchema = z.string().regex(/^[a-z][a-z0-9_]*$/u).max(63);
const relationSchema = z.string().regex(/^[a-z][a-z0-9_]*$/u).max(120);
/** Normalised identifiers an observation can carry. Only the joining kinds merge observations. */
export const TWIN_ENTITY_KEY_KINDS = [
    "name",
    "provider_code",
    "email",
    "email_domain",
    "website_domain",
    "company_number",
    "vat_number",
];
export const twinEntityKeyKindSchema = z.enum(TWIN_ENTITY_KEY_KINDS);
/** Exact shared identifiers that make two observations of one type the same entity. Names never do. */
export const TWIN_ENTITY_JOINING_KEY_KINDS = [
    "email",
    "website_domain",
    "company_number",
    "vat_number",
];
export const twinEntityRoleSchema = z.enum(["person", "organisation"]);
/**
 * An identity marker on one projection relation: its rows are entities of a type. Markers are
 * defined once per connector, never per tenant; Twin Build may later rename a type for a tenant.
 */
export const twinEntityIdentityMarkerSchema = z.object({
    provider: connectorProviderSchema,
    relation: relationSchema,
    entityType: slugSchema,
    label: z.string().trim().min(1).max(80),
    pluralLabel: z.string().trim().min(1).max(80),
    description: z.string().trim().min(1).max(300),
    role: twinEntityRoleSchema.optional(),
    /** Columns that identify a row within the provider; joined with ':' into the external id. */
    idColumns: z.array(columnSchema).min(1).max(4),
    /**
     * Other columns that identify a row one-to-one (a provider's numeric twin of its identifier), so a relationship
     * that references the entity by them resolves to it through this relation.
     */
    alternateIdColumns: z.array(z.array(columnSchema).min(1).max(4)).max(4).optional(),
    /** Columns shown as the entity's name, first non-empty wins. Every non-empty one is a name key. */
    displayColumns: z.array(columnSchema).min(1).max(4),
    /** Columns joined with spaces into a name ahead of `displayColumns` (a first name and a surname). */
    nameParts: z.array(columnSchema).min(2).max(4).optional(),
    /** Columns holding identifiers shared across systems, with their normaliser. */
    keys: z.array(z.object({ column: columnSchema, kind: twinEntityKeyKindSchema }).strict()).max(12).default([]),
    /**
     * Columns naming, as its `idColumns` do, the record this one was merged into in its own system (a Xero contact merged
     * into the one it duplicated). A person made that merge there, so the two are one entity, whatever their identifiers say.
     */
    mergedIntoColumns: z.array(columnSchema).min(1).max(4).optional(),
    /** The provider's status codes for a record no longer in use (an archived Xero contact): it never names its entity. */
    inactive: z.object({ column: columnSchema, values: z.array(z.string().trim().min(1).max(80)).min(1).max(8) }).strict()
        .optional(),
    /** Date column that places a record-like entity (an engagement) in a period, for facts by year. */
    periodColumn: columnSchema.optional(),
    /** Columns that say how to reach the entity and what it is to the organisation, for its page; they join nothing. */
    details: z.object({
        emails: z.array(columnSchema).max(4).optional(),
        phones: z.array(columnSchema).max(4).optional(),
        websites: z.array(columnSchema).max(4).optional(),
        vatNumbers: z.array(columnSchema).max(4).optional(),
        companyNumbers: z.array(columnSchema).max(4).optional(),
        /** Boolean columns that, when true, give the entity a role. */
        roles: z.array(z.object({ column: columnSchema, role: z.enum(["customer", "supplier"]) }).strict()).max(4).optional(),
    }).strict().optional(),
}).strict();
/**
 * A marker on a declared relationship: rows of the source relation reference an entity of the
 * target relation's type, in the role `linkType`. When the source row also carries the target's
 * name (an Overture booking names its promoter company), `nameColumns` let the reference create the
 * entity even before, or without, a row in the target relation.
 */
export const twinEntityReferenceMarkerSchema = z.object({
    provider: connectorProviderSchema,
    relation: relationSchema,
    relationship: slugSchema,
    linkType: slugSchema,
    nameColumns: z.array(columnSchema).max(4).default([]),
}).strict();
/**
 * A marker on a relation whose rows associate two entities and are no entity themselves (an Overture company's people):
 * each row links the entity its `from` relationship references to the entity its `to` relationship references, in the
 * role `linkType`. `roleColumn` holds the row's own words for the association, such as a contact's job title there.
 */
export const twinEntityAssociationMarkerSchema = z.object({
    provider: connectorProviderSchema,
    relation: relationSchema,
    from: slugSchema,
    to: slugSchema,
    linkType: slugSchema,
    roleColumn: columnSchema.optional(),
}).strict();
/**
 * A marker on a relation whose rows carry money for the entity a declared relationship names (a Xero contact's invoices
 * and bills). The entity's facts total one of the relation's declared additive measures, the definition governed
 * products pin, split as the measure's additivity partitions require: by `currencyColumn`, and by the one other
 * partition's declared value meanings (a sales invoice or a bill). With `periodColumn` the totals are per year and all
 * time; without it the measure is a current amount, one total for now. `label` names the figure, with `{values}`
 * standing for the partition's meaning in the plural ("Outstanding {values}": "Outstanding bills"). Where a meaning's
 * plural would not read ("Money spent"), `valueLabels` names each code's row itself, and codes it leaves out are not
 * shown. Each row says where its figure comes from ("From Xero bills"), and `valueNotes` adds what a code's row covers
 * where its name alone would not say ("card and direct payments, not bills or transfers").
 */
export const twinEntityMoneyMarkerSchema = z.object({
    provider: connectorProviderSchema,
    relation: relationSchema,
    relationship: slugSchema,
    measure: columnSchema,
    currencyColumn: columnSchema,
    periodColumn: columnSchema.optional(),
    label: z.string().trim().min(1).max(80),
    valueLabels: z.record(z.string().trim().min(1).max(40), z.string().trim().min(1).max(60)).optional(),
    valueNotes: z.record(z.string().trim().min(1).max(40), z.string().trim().min(1).max(120)).optional(),
}).strict();
/** How a link between relations was established. */
export const twinEntityLinkBasisSchema = z.enum(["declared", "discovered"]);
/**
 * A link's state. `active` links derive entity links and facts; `partial` links are real but cover
 * only part of either side (only some suppliers are artists) and derive links for the values that
 * resolve; `candidate` links await a decision and derive nothing; `failing` links are contradicted
 * by the data and unused; `untestable` links point at a relation that holds no rows.
 */
export const twinEntityLinkStatusSchema = z.enum(["active", "partial", "candidate", "failing", "untestable"]);
/** The count-only evidence a link decision reads: metadata and counts, never values. */
export const twinEntityLinkEvidenceSchema = z.object({
    declared: z.boolean(),
    source: z.object({ grain: z.string(), column: z.string(), sqlType: z.string(), description: z.string() }).strict(),
    target: z.object({ grain: z.string(), column: z.string(), sqlType: z.string(), description: z.string(), rows: z.number().int().nonnegative() }).strict(),
    /** Distinct non-empty referencing values measured (all of them, or a sample for discovery). */
    referencingValues: z.number().int().nonnegative(),
    sampled: z.boolean(),
    /** Share of those values found among the target's identifiers. */
    resolvingShare: z.number().min(0).max(1).nullable(),
    /** Share of the target's rows that at least one measured value refers to. */
    targetShareReferenced: z.number().min(0).max(1).nullable(),
    /**
     * Share of the measured values expected to resolve by coincidence, for integer identifiers: the
     * share that resolves after every value is shifted by a few units, which keeps each value where it
     * lies among the target's identifiers. Null for text identifiers, where chance matches are negligible.
     */
    chanceShare: z.number().min(0).max(1).nullable(),
    /**
     * Share of the values chance would not resolve that do resolve, (resolving - chance) / (1 - chance),
     * at least 0: near 0 when the overlap is coincidence, near 1 when nearly every value is a reference.
     * Null when either share is null.
     */
    beyondChanceShare: z.number().min(0).max(1).nullable(),
}).strict();
export const TWIN_ENTITY_LINK_CHOICES = ["reference", "partial_reference", "unrelated"];
/**
 * The decision model's answer for one link. A column's candidate links are decided by one
 * target-choice question: the chosen target's link is a reference or partial reference with the
 * probability that the column refers to that target; every other candidate is unrelated with the
 * probability that the column does not refer to it.
 */
export const twinEntityLinkDecisionSchema = z.object({
    choice: z.enum(TWIN_ENTITY_LINK_CHOICES),
    probability: z.number().min(0).max(1),
    model: z.string(),
    decidedAt: z.number().int().nonnegative(),
    /**
     * The answer awaits review and does not set the link's status: it was below the acting
     * probability, or it contradicts strong count evidence. A discovered link stays a candidate and a
     * declared link keeps its provisional status.
     */
    review: z.boolean(),
}).strict();
/** A verified join path between two relations, with the share of referencing values that resolve. */
export const twinEntityLinkDefinitionSchema = z.object({
    id: z.string().min(1).max(200),
    linkType: slugSchema,
    /** How the referencing side names the link: the role it points to ("Promoter" on an engagement). */
    label: z.string().min(1),
    /** How the referenced side names it: who points to it in that role ("Engagements as promoter"). */
    inverseLabel: z.string().min(1),
    basis: twinEntityLinkBasisSchema,
    status: twinEntityLinkStatusSchema,
    /** Entity type of the referencing rows, or null when they are records (journal lines, invoices). */
    fromEntityType: slugSchema.nullable(),
    sourceProvider: connectorProviderSchema,
    sourceRelation: relationSchema,
    sourceColumns: z.array(columnSchema).min(1),
    toEntityType: slugSchema,
    targetProvider: connectorProviderSchema,
    targetRelation: relationSchema,
    targetColumns: z.array(columnSchema).min(1),
    /** Share of distinct non-null referencing values found in the target, 0..1; null when untestable. */
    coverage: z.number().min(0).max(1).nullable(),
    referencingValues: z.number().int().nonnegative(),
    /** Date column of the referencing relation used for facts by period, when it has one. */
    periodColumn: columnSchema.nullable(),
    /** The decision model's answer for this evidence; null when undecided and the status is provisional. */
    decision: twinEntityLinkDecisionSchema.nullable(),
    /**
     * Why a rule rather than a confident decision set the status: chance explains the overlap (the
     * link fails whatever the decision says), or the decision awaits review. Null otherwise.
     */
    statusReason: z.string().max(500).nullable(),
}).strict();
export const twinEntityTypeSummarySchema = z.object({
    slug: slugSchema,
    label: z.string(),
    pluralLabel: z.string(),
    description: z.string(),
    role: twinEntityRoleSchema.nullable(),
    members: z.array(z.object({
        provider: connectorProviderSchema,
        relation: relationSchema,
        idColumns: z.array(columnSchema),
        observations: z.number().int().nonnegative(),
    }).strict()),
    entities: z.number().int().nonnegative(),
    /** Entities observed in two or more systems. */
    crossSystemEntities: z.number().int().nonnegative(),
}).strict();
export const twinEntityTypesResponseSchema = z.object({
    types: z.array(twinEntityTypeSummarySchema),
    links: z.array(twinEntityLinkDefinitionSchema),
    derivedAt: z.number().int().nonnegative().nullable(),
}).strict();
export const twinEntityObservationSchema = z.object({
    provider: connectorProviderSchema,
    relation: relationSchema,
    externalId: z.string(),
    observedName: z.string().nullable(),
    /**
     * How the record became this entity's: its own row, a reference naming it, a shared identifier, a person's merge, or
     * an accepted Twin Matching link.
     */
    derivation: z.enum(["marker", "reference", "key", "correction", "identity_link"]),
}).strict();
export const twinEntityFactSchema = z.object({
    fact: z.string(),
    /**
     * The fact as a sentence: "Promoter companies through engagements as artist contact". A money fact's, which its measure
     * and currency already name, says where its figure comes from: "From Xero bills".
     */
    label: z.string(),
    /** A year, "unknown", "all" for an all-time total, or "now" for a current amount such as what is outstanding. */
    period: z.string(),
    value: z.number(),
    /** Its short name as a row: "Promoter companies". */
    measure: z.string().optional(),
    /** The link it is read through, with the role the entity plays on it ("As artist contact"); null for a fact about the
     * entity itself, such as a counterparty's invoices. */
    via: z.object({ link: z.string(), label: z.string() }).strict().nullable().optional(),
    /** A money fact's currency (ISO code): totals are kept per currency, never added across. Absent for counts. */
    currency: z.string().regex(/^[A-Z]{3}$/u).optional(),
}).strict();
export const twinEntitySummarySchema = z.object({
    id: z.string().regex(/^ent_[a-f0-9]{24}$/u),
    entityType: slugSchema,
    typeLabel: z.string(),
    displayName: z.string(),
    /** Other spellings the sources hold for this entity. */
    names: z.array(z.string()),
    /** Systems the entity is observed in, as provider names. */
    providers: z.array(connectorProviderSchema),
    /** The date a record-like entity (an engagement) occurred on, telling same-named ones apart. */
    date: isoDateSchema.optional(),
}).strict();
export const twinEntityLinkedSchema = z.object({
    linkType: z.string(),
    /** The group's name on this entity's page, for its direction: "Promoter", or "Engagements as promoter". */
    label: z.string().min(1),
    direction: z.enum(["outgoing", "incoming"]),
    entityType: slugSchema,
    count: z.number().int().nonnegative(),
    /** Up to ten of the group's entities. `role` is a record's own words for the link: a contact's job title at a company. */
    sample: z.array(z.object({ id: z.string(), displayName: z.string(), date: isoDateSchema.optional(),
        role: z.string().min(1).optional() }).strict()).max(10),
}).strict();
/** How to reach an entity and what it is to the organisation, from the source records the viewer may read. */
export const twinEntityContactSchema = z.object({
    emails: z.array(z.string()),
    phones: z.array(z.string()),
    websites: z.array(z.string()),
    vatNumbers: z.array(z.string()),
    companyNumbers: z.array(z.string()),
    roles: z.array(z.enum(["customer", "supplier"])),
}).strict();
/** A record of the entity's that an attention rule matched and that still needs a look ("Invoice INV-0042 is overdue"). */
export const twinEntityAttentionSchema = z.object({
    itemId: z.string().min(1).max(200),
    ruleId: z.string().min(1).max(200),
    /** The rule's items together, in the plural ("Overdue invoices"). */
    ruleLabel: z.string().min(1).max(120),
    title: z.string().min(1).max(200),
    reason: z.string().min(1).max(300),
    raisedAt: z.number().int().nonnegative(),
}).strict();
export const twinEntityDetailSchema = twinEntitySummarySchema.extend({
    observations: z.array(twinEntityObservationSchema),
    keys: z.array(z.object({ kind: twinEntityKeyKindSchema, value: z.string() }).strict()),
    contact: twinEntityContactSchema,
    linked: z.array(twinEntityLinkedSchema),
    facts: z.array(twinEntityFactSchema),
    /** Open attention items on the entity's records the viewer may read; absent when none. */
    attention: z.array(twinEntityAttentionSchema).min(1).optional(),
}).strict();
/** An organisation admin's "never" for one entity under one attention rule; null clears it. */
export const twinAttentionOverrideRequestSchema = z.object({
    ruleId: z.string().trim().min(1).max(200),
    entityId: z.string().regex(/^ent_[a-f0-9]{24}$/u),
    mode: z.literal("never").nullable(),
}).strict();
export const twinAttentionOverrideResponseSchema = z.object({
    ruleId: z.string(),
    entityId: z.string(),
    mode: z.literal("never").nullable(),
    /** Open items of the entity under the rule that the override resolved. */
    resolved: z.number().int().nonnegative(),
}).strict();
/** A directory's order: name A–Z, most records first, or the most recent year of records first. */
export const twinEntitySortSchema = z.enum(["name", "records", "latest"]);
export const twinEntitySearchQuerySchema = z.object({
    q: z.string().trim().max(200).optional(),
    type: slugSchema.optional(),
    /**
     * With `linkType` and `direction`: only the entities in one of this entity's relationship groups, as its
     * detail's `linked` entry names the group.
     */
    linkedTo: z.string().regex(/^ent_[a-f0-9]{24}$/u).optional(),
    linkType: z.string().trim().min(1).max(200).optional(),
    direction: z.enum(["outgoing", "incoming"]).optional(),
    limit: z.coerce.number().int().min(1).max(100).default(25),
    sort: twinEntitySortSchema.default("name"),
    /** The previous page's `nextCursor`. */
    cursor: z.string().regex(/^\d{1,7}$/u).optional(),
}).strict().refine((query) => [query.linkedTo, query.linkType, query.direction].every((value) => value === undefined)
    || [query.linkedTo, query.linkType, query.direction].every((value) => value !== undefined), { message: "linkedTo, linkType and direction name a relationship group together", path: ["linkedTo"] });
/** A directory row: the entity, and how much its systems hold that refers to it. */
export const twinEntityListItemSchema = twinEntitySummarySchema.extend({
    /** Records in its systems that refer to it, over all time. */
    records: z.number().int().nonnegative().optional(),
    /** The latest year up to this one that any of those records falls in; later ones are planned work, in the facts. */
    latestYear: z.string().regex(/^\d{4}$/u).optional(),
}).strict();
export const twinEntitySearchResponseSchema = z.object({
    entities: z.array(twinEntityListItemSchema),
    truncated: z.boolean(),
    /** Present while more entities follow; pass it back as `cursor` for the next page. */
    nextCursor: z.string().optional(),
}).strict();
/** One record link's records for an entity: the link is a `records:<link>` fact's link. */
export const twinEntityRecordsQuerySchema = z.object({
    link: z.string().trim().min(1).max(300),
    limit: z.coerce.number().int().min(1).max(50).default(25),
    /** The previous page's `nextCursor`. */
    cursor: z.string().regex(/^\d{1,7}$/u).optional(),
}).strict();
/**
 * A column a person reads a record by, picked from its relation's catalogue: when it happened, what it
 * is called, its kind and status as the provider codes them, and its amount in `currency` when known.
 */
export const twinEntityRecordColumnSchema = z.object({
    key: z.enum(["date", "label", "type", "status", "amount"]),
    label: z.string().min(1),
    /** The relation's own column, for a reader who wants the source name. */
    column: z.string().min(1),
    /**
     * What the provider documents each coded value to mean ("ACCPAY": "Bill: accounts payable, the organisation owes a
     * supplier"); absent where it documents none, when the raw value is all there is.
     */
    meanings: z.record(z.string(), z.string()).optional(),
}).strict();
const recordValueSchema = z.union([z.string(), z.number(), z.null()]);
export const twinEntityRecordsResponseSchema = z.object({
    /** Where the records live, as the facts name them: "Xero bank transactions". */
    label: z.string().min(1),
    provider: connectorProviderSchema,
    relation: z.string().min(1),
    columns: z.array(twinEntityRecordColumnSchema),
    records: z.array(z.object({
        /** The record's key in its relation. */
        id: z.string(),
        values: z.object({
            date: recordValueSchema.optional(),
            label: recordValueSchema.optional(),
            type: recordValueSchema.optional(),
            status: recordValueSchema.optional(),
            amount: recordValueSchema.optional(),
            currency: recordValueSchema.optional(),
        }).strict(),
    }).strict()),
    /** Every record of this link that refers to the entity, less those its provider voided or deleted. */
    total: z.number().int().nonnegative(),
    nextCursor: z.string().optional(),
}).strict();
/**
 * The discussions an entity page lists (docs/specs/shared-review-reviewers.md, "Discussions"), latest activity first:
 * its own thread, and the threads of matches its records took part in. Each opens as a feed thread with no channel.
 */
export const twinEntityDiscussionsResponseSchema = z.object({
    discussions: z.array(z.object({
        threadId: z.string().min(1),
        subjectType: z.enum(["entity", "entity_match"]),
        subjectId: z.string().min(1).max(600),
        updatedAt: z.number().int().nonnegative(),
        /** Comments not deleted. */
        commentCount: z.number().int().nonnegative(),
        /** What a match is about, as a person reads it ("Acme Ltd ↔ ACME LIMITED"); null for the entity's own thread. */
        title: z.string().min(1).nullable(),
        /** A match's two sides as the entity layer names their records: what the discussion is about. */
        sides: z.array(z.object({
            side: z.enum(["left", "right"]),
            provider: z.string().min(1),
            relation: z.string().min(1),
            externalId: z.string().min(1),
            /** The record's name as observed, when it has one. */
            name: z.string().nullable(),
            /** The entity the record belongs to now, when the entity layer holds it. */
            entityId: z.string().nullable(),
        }).strict()).max(2).optional(),
    }).strict()).max(200),
}).strict();
const entityIdSchema = z.string().regex(/^ent_[a-f0-9]{24}$/u);
const correctionReasonSchema = z.string().trim().min(1).max(2000);
/**
 * A person's correction to the entity layer, made on an entity's page by an org admin: `not_entity` says the
 * entity's records are not an entity at all; `merge` says it and `entityId` are one entity. The next entity pass
 * applies it, and undoing it restores what the records' identifiers say.
 */
export const twinEntityCorrectionRequestSchema = z.discriminatedUnion("kind", [
    z.object({ kind: z.literal("not_entity"), reason: correctionReasonSchema.optional() }).strict(),
    z.object({ kind: z.literal("merge"), entityId: entityIdSchema, reason: correctionReasonSchema.optional() }).strict(),
]);
/** A correction as recorded: the records it names hold it through re-derivation, whatever IDs their entities get. */
export const twinEntityCorrectionSchema = z.object({
    id: z.string().regex(/^ecor_[0-9a-z]{26}$/u),
    kind: z.enum(["not_entity", "merge"]),
    entityType: slugSchema,
    /** The entities as the person saw them: the one corrected, then the one merged into it. */
    entityIds: z.array(entityIdSchema).min(1).max(2),
    names: z.array(z.string()).min(1).max(2),
    /** The source records the person could see on those entities. */
    records: z.array(z.object({ provider: connectorProviderSchema, relation: relationSchema, externalId: z.string() }).strict())
        .min(1),
    reason: z.string().nullable(),
    /** The user who made it, and who undid it. */
    madeBy: z.string(),
    madeAt: z.number().int().nonnegative(),
    undoneBy: z.string().nullable(),
    undoneAt: z.number().int().nonnegative().nullable(),
    /** Whether the entities reflect it yet (or its undoing): an entity pass has finished since. */
    applied: z.boolean(),
}).strict();
/** An organisation's corrections, newest first: those on one entity's records, live or undone ones, or all. */
export const twinEntityCorrectionsQuerySchema = z.object({
    entityId: entityIdSchema.optional(),
    state: z.enum(["live", "undone", "all"]).default("all"),
    limit: z.coerce.number().int().min(1).max(200).default(50),
}).strict();
export const twinEntityCorrectionsResponseSchema = z.object({
    corrections: z.array(twinEntityCorrectionSchema),
}).strict();
/** A source record named as Twin Matching names a warehouse side: "connector_warehouse.<relation>" and its ID there. */
export const twinRecordEntityQuerySchema = z.object({
    system: z.string().trim().regex(/^connector_warehouse\.[a-z][a-z0-9_]*$/u),
    externalId: z.string().trim().min(1).max(500),
}).strict();
/** The entity the record is observed in, as the viewer may see it; null when it is none or not the viewer's to read. */
export const twinRecordEntityResponseSchema = z.object({
    entity: twinEntitySummarySchema.nullable(),
}).strict();
/** Names as a page shows them (a record's cell, a contact's name), to learn which entity each names. */
export const twinEntityNamesRequestSchema = z.object({
    names: z.array(z.string().trim().min(1).max(300)).min(1).max(100),
}).strict();
/**
 * The entity each name names, as the viewer may see it. A name that names no entity, or several, is left out, so a
 * page shows a name as plain text rather than guess.
 */
export const twinEntityNamesResponseSchema = z.object({
    matches: z.array(z.object({ name: z.string(), entity: twinEntitySummarySchema }).strict()),
}).strict();
export const twinEntityResolveRequestSchema = z.object({
    text: z.string().trim().min(1).max(8_000),
}).strict();
/**
 * A governed product whose rows name entities of a type: `field` holds the IDs of the type's member relation
 * `provider`.`relation`, the IDs an entity's observations carry, so the product filters on an entity's own IDs.
 */
export const twinEntityBoundProductSchema = z.object({
    productSlug: z.string().regex(/^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/u).max(80),
    field: columnSchema,
    provider: connectorProviderSchema,
    relation: relationSchema,
}).strict();
/**
 * What a question names: entity types it mentions (with their verified join paths and the governed products bound to
 * them) and the entities whose name or spelling it contains. Ambiguous names list their candidates instead.
 */
export const twinEntityResolveResponseSchema = z.object({
    types: z.array(z.object({
        type: twinEntityTypeSummarySchema,
        links: z.array(twinEntityLinkDefinitionSchema),
        /** Absent when no live product is bound to the type. */
        products: z.array(twinEntityBoundProductSchema).min(1).max(32).optional(),
    }).strict()),
    entities: z.array(twinEntityDetailSchema),
    ambiguous: z.array(z.object({ name: z.string(), candidates: z.array(twinEntitySummarySchema) }).strict()),
}).strict();
/**
 * The executor's route to entities, authorised by the run's lease and read as the run's subject:
 * `resolve` names what a question mentions; `context` looks one entity up by ID or name.
 */
const leaseId = z.string().trim().min(1).max(200);
const workerLease = { runId: leaseId, tenantSlug: leaseId, workerId: leaseId, attemptNumber: z.number().int().positive() };
export const workerTwinEntityRequestSchema = z.discriminatedUnion("operation", [
    z.object({ ...workerLease, operation: z.literal("resolve"), input: twinEntityResolveRequestSchema }).strict(),
    z.object({
        ...workerLease,
        operation: z.literal("context"),
        input: z.object({
            /** An entity ID (`ent_…`) or a name or spelling. */
            entity: z.string().trim().min(1).max(300),
            type: slugSchema.optional(),
        }).strict(),
    }).strict(),
]);
export const workerTwinEntityResponseSchema = z.discriminatedUnion("operation", [
    z.object({ operation: z.literal("resolve"), result: twinEntityResolveResponseSchema }).strict(),
    z.object({
        operation: z.literal("context"),
        entities: z.array(twinEntityDetailSchema),
        /** Other entities with the same name when the name is ambiguous, without details. */
        candidates: z.array(twinEntitySummarySchema),
        truncated: z.boolean(),
        /**
         * Only when a name matched nothing: the spellings and initials searched, and what the reader's index covers
         * (visible entities by type and the readable sources they are observed in), so a miss is an answer.
         */
        notFound: z.object({
            spellings: z.array(z.string().min(1)),
            initials: z.array(z.string().min(1)),
            coverage: z.array(z.object({
                entityType: slugSchema,
                typeLabel: z.string().min(1),
                entities: z.number().int().nonnegative(),
                sources: z.array(z.string().min(1)),
            }).strict()),
        }).strict().optional(),
    }).strict(),
]);
